import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ParticlesBG from '../../Shared/ParticlesBG';
import './HelpCenter.scss';

const faqData = [
  {
    category: 'Getting Started',
    questions: [
      {
        q: 'What is TRND?',
        a: 'TRND is an AI-powered marketing platform that helps businesses create effective marketing strategies, generate content, and connect with professional marketing vendors. Our tools include Marketing Genius for strategy planning, AI content generators, and a marketplace of vetted marketing professionals.'
      },
      {
        q: 'How do I create an account?',
        a: 'Click the "Sign Up" button in the top right corner. You can sign up using your email address or through Google authentication. Once registered, you\'ll have access to our free tier features immediately.'
      },
      {
        q: 'What\'s included in the free tier?',
        a: 'The free tier includes 3 AI messages per month with Marketing Genius (basic reports only), 10 tokens for content generation, access to the marketplace (view only), and the ability to save 0 reports.'
      }
    ]
  },
  {
    category: 'Marketing Genius',
    questions: [
      {
        q: 'What is Marketing Genius?',
        a: 'Marketing Genius is our AI-powered marketing strategy tool. It analyzes your business needs and provides personalized marketing recommendations, campaign strategies, budget allocations, and ROI projections. Think of it as having a marketing consultant available 24/7.'
      },
      {
        q: 'What are the different complexity levels?',
        a: 'Basic provides quick, actionable recommendations. Intermediate offers detailed analysis with comprehensive strategies. Advanced gives you in-depth strategic analysis with data models, ROI projections, and implementation plans. Your subscription tier determines which levels you can access.'
      },
      {
        q: 'Can I export my marketing reports?',
        a: 'Yes! Basic and higher tiers can export to PDF. Standard and higher tiers can also export to Excel. Pro and Agency tiers have unlimited exports with no watermarks.'
      },
      {
        q: 'What are tokens and how do they work?',
        a: 'Tokens are credits used for AI operations. Different actions cost different amounts: Marketing Genius basic reports cost 5 tokens, advanced reports cost 30 tokens, and content generation varies by type. Tokens reset monthly on your billing date.'
      }
    ]
  },
  {
    category: 'Subscriptions & Billing',
    questions: [
      {
        q: 'What subscription plans are available?',
        a: 'We offer Free, Basic ($19.99/mo), Standard ($49.99/mo), and Pro ($99.99/mo) plans for customers. Agency plans are available for marketing professionals. Each tier offers different limits on AI messages, tokens, and features.'
      },
      {
        q: 'How do I upgrade my subscription?',
        a: 'Go to your Dashboard or the Subscriptions page and click "Upgrade". You\'ll be guided through the process. Upgrades take effect immediately, and you\'ll be charged the prorated difference for the current billing period.'
      },
      {
        q: 'Can I cancel my subscription?',
        a: 'Yes, you can cancel anytime from your account settings. Your access continues until the end of your current billing period. See our Refund Policy for details on cancellations.'
      },
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit cards (Visa, MasterCard, American Express) through our secure payment processor Stripe. Payments are processed by Zoho Billing for enhanced security.'
      }
    ]
  },
  {
    category: 'Marketplace',
    questions: [
      {
        q: 'How does the marketplace work?',
        a: 'Our marketplace connects you with vetted marketing professionals. Browse services, compare vendors, and place orders directly through the platform. We handle payments and provide dispute resolution if needed.'
      },
      {
        q: 'How are vendors vetted?',
        a: 'All vendors go through an application process where we verify their credentials, review their portfolio, and conduct background checks. We also monitor ongoing performance through our reputation system.'
      },
      {
        q: 'What if I\'m not satisfied with a vendor\'s work?',
        a: 'We have a dispute resolution process. First, try to resolve the issue directly with the vendor. If unsuccessful, contact our support team within 14 days of delivery. We\'ll review the case and may issue a partial or full refund.'
      }
    ]
  },
  {
    category: 'Technical Support',
    questions: [
      {
        q: 'I\'m having trouble logging in. What should I do?',
        a: 'First, try resetting your password using the "Forgot Password" link. If that doesn\'t work, clear your browser cache and cookies, then try again. If issues persist, contact support with your account email.'
      },
      {
        q: 'The AI isn\'t responding. What\'s wrong?',
        a: 'This could be due to high server load or a temporary outage. Wait a few minutes and try again. If the problem persists, check our status page or contact support.'
      },
      {
        q: 'How do I report a bug?',
        a: 'Use the contact form below or email support@trnd.co with details about the issue, including what you were doing, what browser you\'re using, and any error messages you saw. Screenshots are helpful!'
      }
    ]
  }
];

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState('Getting Started');
  const [expandedQuestions, setExpandedQuestions] = useState({});

  const toggleQuestion = (category, index) => {
    const key = `${category}-${index}`;
    setExpandedQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const filteredFaq = searchQuery
    ? faqData.map(category => ({
        ...category,
        questions: category.questions.filter(
          q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
               q.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.questions.length > 0)
    : faqData;

  return (
    <div className="help-center-page">
      <ParticlesBG />
      <div className="help-center-container">
        <header className="help-header">
          <h1>Help Center</h1>
          <p>Find answers to common questions or contact our support team</p>
          
          <div className="search-box">
            <input
              type="text"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
        </header>

        <div className="help-content">
          <aside className="category-nav">
            <h3>Categories</h3>
            <ul>
              {faqData.map(category => (
                <li 
                  key={category.category}
                  className={expandedCategory === category.category ? 'active' : ''}
                  onClick={() => setExpandedCategory(category.category)}
                >
                  {category.category}
                </li>
              ))}
            </ul>
            
            <div className="quick-links">
              <h3>Quick Links</h3>
              <ul>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                <li><Link to="/terms-and-conditions">Terms & Conditions</Link></li>
                <li><Link to="/refund-policy">Refund Policy</Link></li>
                <li><Link to="/contact">Contact Us</Link></li>
              </ul>
            </div>
          </aside>

          <main className="faq-content">
            {filteredFaq.map(category => (
              <div key={category.category} className="faq-category">
                <h2>{category.category}</h2>
                <div className="faq-list">
                  {category.questions.map((item, index) => (
                    <div 
                      key={index} 
                      className={`faq-item ${expandedQuestions[`${category.category}-${index}`] ? 'expanded' : ''}`}
                    >
                      <button 
                        className="faq-question"
                        onClick={() => toggleQuestion(category.category, index)}
                      >
                        <span>{item.q}</span>
                        <span className="toggle-icon">
                          {expandedQuestions[`${category.category}-${index}`] ? '−' : '+'}
                        </span>
                      </button>
                      {expandedQuestions[`${category.category}-${index}`] && (
                        <div className="faq-answer">
                          <p>{item.a}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </main>
        </div>

        <section className="contact-section">
          <h2>Still need help?</h2>
          <p>Our support team is here to assist you</p>
          <div className="contact-options">
            <Link to="/contact" className="contact-card">
              <span className="contact-icon">✉️</span>
              <h3>Email Support</h3>
              <p>Get a response within 24 hours</p>
            </Link>
            <div className="contact-card">
              <span className="contact-icon">📖</span>
              <h3>Documentation</h3>
              <p>Detailed guides and tutorials</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
